import { EventEmitter } from '../util/eventemitter';

export class PartyDataMember {
  disconnectedTime: number;
  serverTag: number;
  serverIdx: number;
  maxHP: number;
  hp: number;
  status: bigint;
  con: number;
  recoverHP: number;
  recoverHPRate: number;
  recoverMP: number;
  recoverMPRate: number;
  stamina: number;
  name: string;

  constructor() {
    this.disconnectedTime = 0;
  }
}

export class PartyData extends EventEmitter {
  exists: boolean;
  members: any[];
  leaderIdx: number;
  leaderTag: number;
  rule: number;
  xp: number;
  level: number;

  static getLevelMaxXP(level: any) {
    return (level * (level + 15)) * 16;
  }

  static Member = PartyDataMember;

  constructor() {
    super();
    this.clear();
  }

create() {
    this.exists = true;
    this.members = [];
    this.rule = 0;
    this.xp = 0;
    this.level = 1;
    GCM.system('New party has formed.');
  }

  clear() {
    this.exists = false;
    this.members = [];
    this.leaderIdx = 0;
    this.leaderTag = 0;
    this.rule = 0;
    this.xp = 0;
    this.level = 0;
    this.emit('changed');
  }

  addMembers(members: any) {
    // The server resends the full roster on every join/leave, so replace-or-insert
    // by serverTag instead of blindly appending (which duplicated existing members).
    for (let i = 0; i < members.length; ++i) {
      const member = members[i];
      const existing = this.members.findIndex((m: any) => m.serverTag === member.serverTag);

      if (existing !== -1) {
        this.members[existing] = member;
      } else {
        GCM.system(member.name + ' has joined the party.');
        this.members.push(member);
      }
    }

    this.emit('changed');
  }

  removeMember(serverTag: any) {
    for (let i = 0; i < this.members.length; ++i) {
      if (this.members[i].serverTag === serverTag) {
        this.members.splice(i, 1);
        return true;
      }
    }

    return false;
  }

  updateMember(serverTag: any, data: any) {
    let member = MC.party.findMemberByTag(serverTag);

    if (member) {
      member.serverIdx = data.serverIdx;
      member.maxHP = data.maxHP;
      member.hp = data.hp;
      member.status = data.status;
      member.con = data.con;
      member.recoverHP = data.recoverHP;
      member.recoverHPRate = data.recoverHPRate;
      member.recoverMP = data.recoverMP;
      member.recoverMPRate = data.recoverMPRate;
      member.stamina = data.stamina;
      this.emit('changed');
    }
  }

  levelup() {
    GCM.system('Party is now level ' + this.level + '!');
  }

  disconnectMember(serverTag: any) {
    let member = this.findMemberByTag(serverTag);

    if (!member) {
      return false;
    }

    GCM.system(member.name + ' has disconnected.');
    member.disconnectedTime = new Date().getTime();
    this.emit('changed');
    return true;
  }

  kickMember(serverTag: any) {
    if (serverTag === MC.uniqueTag) {
      GCM.system('You were kicked from the party.');
      MC.party.clear();
    } else {
      let member = this.findMemberByTag(serverTag);

      if (member) {
        GCM.system(member.name + ' has been kicked from the party.');
      }

      this.removeMember(serverTag);
      this.emit('changed');
    }
  }

  leaveMember(serverTag: any) {
    let member = this.findMemberByTag(serverTag);

    if (member) {
      GCM.system(member.name + ' has left the party.');
    }

    this.removeMember(serverTag);
    this.emit('changed');
  }

  setLeaderByIdx(serverIdx: any) {
    if (serverIdx === MC.serverObjectIdx) {
      this.setLeader(MC.serverObjectIdx, MC.uniqueTag);
    } else {
      let member = this.findMemberByIdx(serverIdx);
      this.setLeader(member.serverIdx, member.serverTag);
    }
  }

  setLeader(serverIdx: any, serverTag: any) {
    this.leaderIdx = serverIdx;
    this.leaderTag = serverTag;

    if (this.leaderTag === MC.uniqueTag) {
      GCM.system('You have been given party leadership.');
    } else {
      let member = this.findMemberByTag(serverTag);

      if (member) {
        GCM.system(member.name + ' has been given party leadership.');
      }
    }

    this.emit('changed');
  }

  setLevelXP(level: any, xp: any) {
    this.xp = xp;
    this.level = level;
    this.emit('changed');
  }

  setRule(rule: any) {
    this.rule = rule;
    GCM.system('Party options have changed.');

    if (rule & PARTY_RULE_ITEM_TO_ORDER) {
      GCM.system('Item Earning Priority: Even Share');
    } else {
      GCM.system('Item Earning Priority: Free-For-All');
    }

    if (rule & PARTY_RULE_EXP_PER_PLAYER) {
      GCM.system('Experience Rate: Equal Share Off');
    } else {
      GCM.system('Experience Rate: Equal Share On');
    }

    this.emit('changed');
  }

  findMemberByIdx(serverIdx: any) {
    for (let i = 0; i < this.members.length; ++i) {
      let member = this.members[i];

      if (member.serverIdx === serverIdx) {
        return member;
      }
    }

    return null;
  }

  findMemberByTag(serverTag: any) {
    for (let i = 0; i < this.members.length; ++i) {
      const member = this.members[i];

      if (member.serverTag === serverTag) {
        return member;
      }
    }

    return null;
  }
}

